import React from 'react';

/** MakeCode Arcade 공식 문서와 동일한 정적 이미지 (https://arcade.makecode.com/asset-packs) */
const ASSET_PACK_IMG_BASE = 'https://arcade.makecode.com/static/asset-packs';

function GuideImage({ file, alt }: { file: string; alt: string }) {
  return (
    <img
      src={`${ASSET_PACK_IMG_BASE}/${file}`}
      alt={alt}
      loading="lazy"
      style={{
        maxWidth: '100%',
        height: 'auto',
        display: 'block',
        borderRadius: '8px',
        border: '1px solid var(--Color-line-normal)',
        marginTop: '8px',
        marginBottom: '12px'
      }}
    />
  );
}

const heading2: React.CSSProperties = {
  margin: '24px 0 8px 0',
  fontSize: '18px',
  fontWeight: 600,
  fontFamily: 'var(--font-family-primary)',
  color: 'var(--Color-text-strong)'
};

const heading3: React.CSSProperties = {
  margin: '16px 0 8px 0',
  fontSize: '15px',
  fontWeight: 600,
  color: 'var(--Color-text-strong)'
};

const body: React.CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: '14px',
  lineHeight: 1.5,
  color: 'var(--Color-text-normal)'
};

const codeBlock: React.CSSProperties = {
  margin: '8px 0 12px 0',
  padding: '12px 14px',
  borderRadius: '8px',
  background: 'var(--Color-background-main)',
  fontFamily: 'monospace',
  fontSize: '13px',
  overflowX: 'auto',
  color: 'var(--Color-text-strong)'
};

/** 에셋 팩 안내 본문 (출처: MakeCode Arcade 공식 문서) */
export const AssetPackHowToGuideContent: React.FC = () => {
  return (
    <div>
      <p
        style={{
          margin: '0 0 16px 0',
          fontSize: '12px',
          color: 'var(--Color-text-normal)',
          lineHeight: 1.5
        }}
      >
        출처:{' '}
        <a
          href="https://arcade.makecode.com/asset-packs"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--Color-fill-brand-color)' }}
        >
          MakeCode Arcade — Asset Packs
        </a>
      </p>

      <h2 style={{ ...heading2, marginTop: 0 }}>에셋 팩이란?</h2>
      <p style={body}>
        에셋 팩은 MakeCode Arcade 프로젝트나 확장으로, 타일맵·타일·이미지·애니메이션·곡 등 에셋 편집기 갤러리에 자산을 추가할 때
        쓰는 형태입니다. 일반 프로젝트처럼 블록이나 코드를 추가하는 것이 아니라 <strong>에셋만</strong> 추가하는 것이 특징이며,
        자바스크립트 편집기로 넘어가지 않아도 설정할 수 있습니다. GitHub는 필수는 아니지만, 확장으로 널리 배포할 때는
        저장소를 쓰는 것이 좋습니다.
      </p>

      <h2 style={heading2}>에셋 팩 만들기</h2>
      <p style={body}>빈 프로젝트로 시작해도 되고, 기존 프로젝트를 그대로 써도 됩니다.</p>

      <h3 style={heading3}>1단계: 이름이 붙은 에셋 만들기</h3>
      <p style={body}>
        다른 사람이 팩을 붙였을 때 갤러리에 보이려면 에셋에 이름이 있어야 합니다. 타일맵·타일은 만들 때 자동으로 이름이 붙지만,
        이미지·애니메이션은 직접 입력해야 합니다. 갤러리에서 마우스를 올렸을 때 보이는 이름이므로 알아보기 쉽게 지어 주세요.
        이미지·애니메이션·타일맵 편집기 하단 입력란에 이름을 적습니다.
      </p>
      <GuideImage file="image-editor.png" alt="이미지 편집기 하단에 에셋 이름을 입력하는 화면" />

      <h3 style={heading3}>2단계: 프로젝트 설정에서 ‘Import as Asset Pack’ 켜기</h3>
      <p style={body}>
        편집기 오른쪽 위 톱니바퀴(⚙️) <strong>Settings</strong>에서 프로젝트 설정을 엽니다.
      </p>
      <GuideImage file="project-settings.png" alt="프로젝트 설정 메뉴(톱니바퀴) 위치" />
      <p style={body}>
        프로젝트 설정에서 <strong>Import as Asset Pack</strong>을 켭니다. 스위치가 오른쪽으로 가고 색이 들어가면 켜진 상태입니다.
      </p>
      <GuideImage file="import-asset-pack.png" alt="Import as Asset Pack 옵션을 켠 상태" />
      <p style={body}>
        설정한 뒤 <strong>Go Back</strong>으로 프로젝트로 돌아갑니다.
      </p>
      <GuideImage file="go-back.png" alt="Go Back 버튼" />

      <h3 style={heading3}>3단계: 프로젝트 공유하기</h3>
      <p style={body}>편집기 오른쪽 위 <strong>Share</strong>를 눌러 공유 링크를 만듭니다.</p>
      <GuideImage file="share-project-button.png" alt="Share 버튼 위치" />
      <p style={body}>
        프로젝트 제목은 고유하고 설명하기 좋게 짓고, 설명은 다른 사람이 이 에셋 팩을 추가할 때도 보이므로 유용하게 적어 주세요.
        완료 후 <strong>Share Project</strong>로 링크를 받습니다.
      </p>
      <GuideImage file="share-project.png" alt="Share Project 창에서 이름과 설명을 입력하는 화면" />
      <p style={{ ...body, fontStyle: 'italic', color: 'var(--Color-text-normal)' }}>
        팁: 썸네일(미리보기 이미지)을 만들어 두면 더 좋습니다.
      </p>

      <h3 style={heading3}>4단계: 링크를 게시하기</h3>
      <p style={body}>
        공유 링크가 나오면 준비 완료입니다.{' '}
        <a
          href="https://forum.makecode.com/c/share-your-arcade-projects-here/5"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--Color-fill-brand-color)' }}
        >
          MakeCode 포럼
        </a>
        등 사람들이 찾을 수 있는 곳에 올리거나, 이 MARU 갤러리에 등록해 주세요.
      </p>

      <h2 style={heading2}>내 프로젝트에 에셋 팩 넣기</h2>
      <p style={body}>
        에셋 팩 공유 주소를 받았다면, 확장(Extensions) 대화상자에 그 주소를 붙여 넣습니다. 카드가 나타나면
        선택해 프로젝트에 추가합니다. 이미지·타일맵·애니메이션 등 에셋 편집기 갤러리를 열면 새 에셋이 맨 위에 보입니다.
      </p>
      <GuideImage file="extensions.png" alt="확장에 공유 링크를 붙여 넣는 대화상자" />
      <GuideImage file="image-gallery.png" alt="갤러리에 추가된 에셋이 상단에 표시되는 예" />

      <h3 style={heading3}>동영상: 에셋 팩 만들기</h3>
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '640px',
          paddingBottom: '56.25%',
          height: 0,
          overflow: 'hidden',
          borderRadius: '8px',
          border: '1px solid var(--Color-line-normal)',
          marginBottom: '12px'
        }}
      >
        <iframe
          title="Create an asset pack and share"
          src="https://www.youtube.com/embed/CjLYl5KJiSk"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none'
          }}
        />
      </div>

      <h2 style={heading2}>타일셋 공유하기</h2>
      <p style={body}>
        타일이 포함된 에셋 팩을 추가하면 타일맵 편집기 타일 팔레트의 <strong>Gallery</strong> 드롭다운에 사용자가 만든 카테고리로
        타일이 나타납니다.
      </p>
      <GuideImage file="tilemap-editor.png" alt="타일맵 편집기에서 갤러리를 여는 화면" />
      <p style={body}>
        프로젝트 이름에 글자·숫자·밑줄(<code>_</code>)이 아닌 문자는 밑줄로 바뀌므로 이름 짓기에 유의하세요.
      </p>
      <p style={body}>
        타일 이름에 <strong>이중 대시(<code>--</code>)</strong> 뒤에 오는 텍스트는 카테고리 이름으로 쓰입니다. 예:
      </p>
      <pre style={codeBlock}>
        {`tile1--WallTiles
tile2--WallTiles
tile3--FloorTiles
tile4--FloorTiles`}
      </pre>
      <p style={body}>에셋 팩을 추가하면 Gallery 드롭다운에 벽 타일·바닥 타일처럼 구분된 항목이 생깁니다.</p>
      <GuideImage file="tiles-gallery.png" alt="갤러리 드롭다운에 WallTiles, FloorTiles 카테고리가 나타난 예" />

      <h2 style={heading2}>배경 이미지·대화창 프레임</h2>
      <p style={body}>
        <code>scene.setBackgroundImage</code> 갤러리에 넣으려면 에셋 이름에 <code>background</code>를 포함하세요.
        예: <code>castle background</code>.
      </p>
      <p style={body}>
        <code>game.setDialogFrame</code> 블록 갤러리에 넣으려면 이름에 <code>dialog</code>를 포함하면 됩니다.
      </p>

      <h2 style={heading2}>확장(Extension)으로 배포하기</h2>
      <p style={body}>
        더 넓게 배포하려면 에셋 팩을 GitHub 저장소로 옮깁니다. 편집기 하단 <strong>GitHub</strong> 버튼으로 로그인하고
        저장소를 만듭니다. <strong>반드시 공개(Public)</strong> 저장소여야 다른 사람이 볼 수 있습니다.
      </p>
      <GuideImage file="github-button.png" alt="편집기 하단 GitHub 버튼" />
      <p style={body}>저장소에 올린 뒤 다음을 확인하세요.</p>
      <ul style={{ ...body, paddingLeft: '20px', marginBottom: '12px' }}>
        <li style={{ marginBottom: '6px' }}>
          <strong>Release</strong> 만들기(Create release).
        </li>
        <li style={{ marginBottom: '6px' }}>
          라이선스 추가. 기본 확장 목록에는 <strong>MIT</strong> 라이선스만 허용됩니다.
        </li>
        <li style={{ marginBottom: '6px' }}>
          <strong>Import as Asset Pack</strong>이 켜져 있는지 확인.
        </li>
      </ul>
      <p style={body}>
        공식 추천 확장 목록에 올리고 싶다면{' '}
        <a
          href="https://github.com/microsoft/pxt-arcade"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--Color-fill-brand-color)' }}
        >
          microsoft/pxt-arcade
        </a>
        저장소에 Pull Request를 열고 <code>targetConfig.json</code>의 <code>approvedRepoLib</code>에 항목을 추가하는 방식으로
        요청할 수 있습니다. MakeCode 팀이 검토합니다.
      </p>

    </div>
  );
};

export default AssetPackHowToGuideContent;
